// src/components/layout/Navbar.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlassIcon, Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useLocale } from "../../contexts/LocaleContext";
import MenuItem from "./MenuItem";
import SearchModal from "../search/SearchModal";
import { normalizeMenuUrl } from "../../utils/menuUrl";

function Navbar() {
  const {
    headerData,
    isLoadingSharedData,
    sharedDataError,
  } = useLocale();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [expandedMobileSubMenus, setExpandedMobileSubMenus] = useState({});

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setExpandedMobileSubMenus({});
    }
  };

  const closeMobileMenuOnNavigate = () => {
    setIsMobileMenuOpen(false);
    setExpandedMobileSubMenus({});
  };

  const handleMobileSubMenuToggle = (itemId) => {
    setExpandedMobileSubMenus((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Mobile menu items renderer
  const renderMobileMenuItems = (items, level = 0) => {
    if (!items || items.length === 0) return null;

    return (
      <ul className={level > 0 ? "pl-4 mt-1 space-y-1" : "space-y-1"}>
        {items.map((item) => {
          const itemKey = item.id ?? item.title;
          const hasSubItems =
            item.sub_menu_items && item.sub_menu_items.length > 0;
          const isExpanded = !!expandedMobileSubMenus[itemKey];

          const menuUrl = normalizeMenuUrl(item.url);

          return (
            <li key={item.id || item.title}>
              {menuUrl && !hasSubItems ? (
                <Link
                  to={menuUrl}
                  className="block px-3 py-2 rounded-md text-base font-medium text-brand-foreground hover:bg-mist-100/80 hover:text-mist-500"
                  onClick={closeMobileMenuOnNavigate}
                >
                  {item.title}
                </Link>
              ) : hasSubItems ? (
                <div className="flex items-stretch w-full">
                  {menuUrl ? (
                    <Link
                      to={menuUrl}
                      className="flex-1 min-w-0 px-3 py-2 rounded-l-md text-base font-medium text-brand-foreground hover:bg-mist-100/80 hover:text-mist-500"
                      onClick={closeMobileMenuOnNavigate}
                    >
                      {item.title}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="flex-1 min-w-0 text-left px-3 py-2 rounded-l-md text-base font-medium text-brand-foreground hover:bg-mist-100/80 hover:text-mist-500 focus:outline-none"
                      onClick={() => handleMobileSubMenuToggle(itemKey)}
                    >
                      {item.title}
                    </button>
                  )}
                  <button
                    type="button"
                    className="shrink-0 px-3 py-2 rounded-r-md text-brand-foreground hover:bg-mist-100/80 focus:outline-none"
                    onClick={() => handleMobileSubMenuToggle(itemKey)}
                    aria-expanded={isExpanded}
                    aria-label={`Expand ${item.title}`}
                  >
                    <svg
                      className={`w-5 h-5 transform transition-transform duration-150 ${
                        isExpanded ? "rotate-180" : "rotate-0"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <span className="block px-3 py-2 text-base font-medium text-gray-500">
                  {item.title}
                </span>
              )}
              {hasSubItems && isExpanded && (
                <div className="mt-1">
                  {renderMobileMenuItems(item.sub_menu_items, level + 1)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  // Logo renderer
  const renderLogo = () => {
    if (isLoadingSharedData && (!headerData || !headerData.logoData)) {
      return (
        <div className="h-9 w-28 bg-gray-200 animate-pulse rounded" />
      );
    }
    if (headerData && headerData.logoData && headerData.logoData.url) {
      const logoSrc = `${headerData.logoData.url}`;
      return (
        <img
          src={logoSrc}
          alt={headerData.logoData.alternativeText || "CAS Systems"}
          className="h-12 md:h-14 lg:h-16 w-auto max-h-full"
        />
      );
    }
    return (
      <span className="font-bold text-2xl md:text-3xl lg:text-4xl text-gray-800">
        CAS Systems
      </span>
    );
  };

  // console.log(headerData); 

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-[31] overflow-visible">
        {/* Top Row: White background with Logo, Search, Language */}
        <div className="bg-white shadow-sm">
          <div className="container mx-auto pl-2 md:pl-4 pr-4 xl:pl-4 xl:pr-6">
            <div className="flex items-center justify-between h-16 md:h-20">
              {/* Logo */}
              <Link
                to="/"
                className="flex-shrink-0"
                onClick={closeMobileMenuOnNavigate}
              >
                {renderLogo()}
              </Link>

              {/* Right side: Search + Mobile Menu Button */}
              <div className="flex items-center space-x-3 md:space-x-4">
                {/* Search Icon */}
                <button
                  type="button"
                  className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
                  aria-label="Search"
                  onClick={() => setIsSearchOpen(true)}
                >
                  <MagnifyingGlassIcon className="h-5 w-5 md:h-6 md:w-6" />
                </button>

                {/* Mobile menu button */}
                <button
                  onClick={toggleMobileMenu}
                  type="button"
                  className="xl:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none"
                  aria-controls="mobile-menu"
                  aria-expanded={isMobileMenuOpen ? "true" : "false"}
                >
                  <span className="sr-only">Open main menu</span>
                  {isMobileMenuOpen ? (
                    <XMarkIcon className="block h-6 w-6" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row: dark lake-green menu bar (Desktop only) */}
        <div className="hidden xl:block overflow-visible bg-gradient-to-r from-mist-500 via-lake-500 to-lake-500">
          <div className="container mx-auto overflow-visible px-4 xl:px-6">
            <ul className="flex items-center justify-center overflow-visible space-x-4 2xl:space-x-6">
              {isLoadingSharedData && headerData.menuItems.length === 0 && (
                <li className="px-4 py-3 text-sm text-white/70">
                  Loading...
                </li>
              )}

              {sharedDataError && (
                <li className="px-4 py-3 text-sm text-red-200">
                  {sharedDataError}
                </li>
              )}

              {!isLoadingSharedData &&
                !sharedDataError &&
                headerData.menuItems.map((item) => (
                  <MenuItem
                    key={item.id || item.title}
                    item={item}
                    isGreenNav={true}
                  />
                ))}
            </ul>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div
            className="xl:hidden absolute top-full left-0 right-0 z-[30] max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-mist-200/80 bg-gradient-to-b from-mist-50 via-white to-lake-50/90 shadow-lg backdrop-blur-sm"
            id="mobile-menu"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {isLoadingSharedData && headerData.menuItems.length === 0 && (
                <div className="block px-3 py-2 rounded-md text-base font-medium text-gray-500">
                  Loading...
                </div>
              )}
              {sharedDataError && (
                <div className="block px-3 py-2 rounded-md text-base font-medium text-red-500">
                  Error:{" "}
                  {typeof sharedDataError === "string"
                    ? sharedDataError
                    : "Failed to load menu"}
                </div>
              )}
              {!isLoadingSharedData &&
                !sharedDataError &&
                headerData.menuItems &&
                headerData.menuItems.length > 0 &&
                renderMobileMenuItems(headerData.menuItems)}
              {!isLoadingSharedData &&
                !sharedDataError &&
                (!headerData.menuItems ||
                  headerData.menuItems.length === 0) && (
                  <div className="block px-3 py-2 rounded-md text-base font-medium text-gray-500">
                    No menu items available.
                  </div>
                )}
            </div>
          </div>
        )}
      </nav>

      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      {/* Spacer for fixed navbar */}
      <div className="h-16 md:h-20 xl:h-[calc(5rem+3rem)]" />
    </>
  );
}

export default Navbar;
